import { createCallsConnection } from "frontend/api";
import { resolver } from "frontend/resolver";
import { partition, propExists } from "frontend/utils/fn-utils";
import { ICallConnection } from "./calls-interface";
import { TrackObject } from "./calls-types";
import { BoardUser, VideoPresence } from "shared/datamodel/schemas";
import { Predicate } from "shared/util/fn-utils";
import { groupBy, join, map, path, pipe } from "rambda";
import tracking from "frontend/tracking";

const DEBUG_VIDEO_ROOM = true; // process.env.NODE_ENV != 'production';

const log = DEBUG_VIDEO_ROOM ? console.log.bind(console, "VIDEO: ") : () => {};

type SessionIdType = string;

type RemoteUserVideoData = {
  readonly clientId: string;
  readonly videoPresence: NonNullable<VideoPresence>;
  readonly userInfo: BoardUser;
};

export type RemoteStreamData = {
  clientId: string;
  tracksInfo: TrackObject[];
  userInfo: BoardUser;
  sessionId: string;
  stream: MediaStream;
  ready: boolean;
  videoMuted: boolean;
  audioMuted: boolean;
  retries: number;
};

const groupTracksByType = groupBy((t: RTCRtpTransceiver) => t.sender.track?.kind ?? "unknown");
const getFirstVideo = path<RTCRtpTransceiver>(["video", 0]);
const getFirstAudio = path<RTCRtpTransceiver>(["audio", 0]);
const getUserName = (t: RemoteUserVideoData) => t.userInfo.name;
const allUserNamesToString = pipe(map(getUserName), join(", "));

function mapGet<K, V>(m: Map<K, V>, key: K | null | undefined) {
  if (key == null) return null;
  if (key == undefined) return null;
  return m.get(key);
}

function find<T>(predicate: Predicate<T>, arr: T[] | null | undefined) {
  if (arr == null || arr == undefined || arr.length == 0) return null;
  return arr.find(predicate) ?? null;
}

//---------------

export type ConnectionStatus = "idle" | "connecting" | "connected" | "failed";

export class VideoRoomHandler {
  private call: ICallConnection;
  private pc: RTCPeerConnection = this.createPC();
  private abort: AbortController = new AbortController();
  private localTracks: TrackObject[] = [];
  private abortPromise = resolver<void>();
  private workerRunning = false;
  private jobQueue: RemoteStreamData[] = [];
  private cancelSubscription: (() => void) | null = null;
  private streamsCache = new Map<SessionIdType, RemoteStreamData>(); // clientid -> remotestreamdata
  private connecting: ConnectionStatus = "idle";

  constructor(documentId: string, private setStatus: (status: ConnectionStatus) => void) {
    this.call = createCallsConnection(documentId, this.abort.signal);
    this.abort.signal.addEventListener("abort", () => this.abortPromise.resolve());
  }

  public get status() {
    return this.connecting;
  }

  public stop() {
    if (this.abort.signal.aborted) {
      return;
    }
    this.abort.abort();
    if (this.call.sessionId && this.localTracks.length > 0) {
      //log('closing tracks', project('mid', this.localTracks))
      //this.call.closeTracks(this.pc.localDescription, project('mid', this.localTracks))
    }
    this.pc.close();
    this.cancelSubscription && this.cancelSubscription();
  }

  private createPC() {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
      bundlePolicy: "max-bundle",
    });
    if (DEBUG_VIDEO_ROOM) {
      pc.addEventListener("connectionstatechange", (ev) => {
        const pc = ev.target as RTCPeerConnection;
        log("pc connection state", pc.connectionState);
      });
      pc.addEventListener("iceconnectionstatechange", (ev) => {
        const pc = ev.target as RTCPeerConnection;
        log("pc ice connection state", pc.iceConnectionState);
      });
      pc.addEventListener("icegatheringstatechange", (ev) => {
        const pc = ev.target as RTCPeerConnection;
        log("pc ice gathering state", pc.iceGatheringState);
      });
    }
    return pc;
  }

  /**
   * This function checks if an error thrown is actually a sign of cancellation (using the AbortSignal).
   * On different browsers and from different contexts we get different errors, so we check for all of them.
   */
  private isCanceled(error: any) {
    return (
      this.abort.signal.aborted ||
      error.type == "abort" ||
      error.name == "AbortError" ||
      error.message == "canceled" ||
      error == "canceled"
    );
  }

  public async connect(localVideo: MediaStream) {
    if (this.connecting == "connected" || this.connecting == "connecting") {
      log("room.connect called, but already ", this.connecting);
      return "in-progress";
    }
    this.connecting = "connecting";
    this.setStatus("connecting");

    try {
      localVideo.getTracks().forEach((track) => this.pc.addTrack(track, localVideo));

      await this.pc.setLocalDescription();
      if (!this.pc.localDescription) {
        throw new Error("setLocalDescription failed");
      }

      const response = await this.call.newCallsSession(this.pc.localDescription);
      if (!response) {
        tracking.trackAnalyticsEvent("video_call_failed", {
          error: "new-session-failed",
        });
        throw new Error("new-session failed");
      }
      const { sessionId, sessionDescription } = response;
      if (!sessionId || !sessionDescription) {
        throw new Error("new-session failed: missing sessionId or sessionDescription");
      }
      log("new session established", response);

      // prepare callback that will wait for ICE connection
      const { resolve, promise } = resolver();
      const connectionChangeCallback = (event: Event | null | undefined) => {
        if (!event) {
          // timeout happened
          this.pc.removeEventListener("connectionstatechange", connectionChangeCallback);
          resolve();
        } else {
          const pc = event.target as RTCPeerConnection;
          // check for final states
          if (pc.connectionState == "failed" || pc.connectionState == "connected") {
            this.pc.removeEventListener("connectionstatechange", connectionChangeCallback);
            resolve();
          }
        }
      };
      this.pc.addEventListener("connectionstatechange", connectionChangeCallback);

      // set remote description - this will start the ICE discovery process
      await this.pc.setRemoteDescription(sessionDescription as any);
      setTimeout(connectionChangeCallback, 15_000);
      await promise;
      if (this.pc.connectionState !== "connected") {
        this.pc.close();
        throw new Error("Cannot establish RTC connection to video server");
      }

      log("ICE finished. sessionId = ", sessionId);

      const tracks = groupTracksByType(this.pc.getTransceivers()),
        video = getFirstVideo(tracks),
        audio = getFirstAudio(tracks);
      // TODO: relax this constraint, because we want to support connecting without sending video/audio
      if (!video || !audio) {
        throw new Error("Expected at least 1 video and 1 audio tracks");
      }

      this.localTracks = [
        {
          location: TrackObject.LocationEnum.Local,
          mid: video.mid || "",
          sessionId: this.call.sessionId,
          trackName: video.sender.track!.id,
        },
        {
          location: TrackObject.LocationEnum.Local,
          mid: audio.mid || "",
          sessionId: this.call.sessionId,
          trackName: audio.sender.track!.id,
        },
      ];

      // Must do this first, because now pc is in 'stable' state. At stable state we can't do setRemoteDescription
      await this.pc.setLocalDescription();

      const reply = await this.call.addTracksToSession(this.localTracks, this.pc.localDescription! as any);

      await this.pc.setRemoteDescription(reply.sessionDescription as any);

      log("handshake done");

      this.connecting = "connected";
      this.setStatus("connected");

      return {
        sessionId: this.call.sessionId,
        videoTrackName: video.sender.track!.id,
        audioTrackName: audio.sender.track!.id,
      };
    } catch (error: any) {
      const canceled = this.isCanceled(error);
      this.connecting = "failed";
      this.setStatus("failed");
      if (!canceled) {
        throw error;
      } else {
        this.pc.close();
        return "canceled";
      }
    }
  }

  public notifyChange(others: RemoteUserVideoData[], setRemoteStreams: (streams: RemoteStreamData[]) => void) {
    log(`received update about users ${allUserNamesToString(others)}`);

    // 1. find new tracks of participants who just joined
    // 2. for existing tracks update their mute state
    const newTracksToPull: RemoteUserVideoData[] = [];
    for (const item of others) {
      const { videoPresence } = item;
      if (!videoPresence.sessionId) {
        log("got item without sessionId", JSON.stringify(item, null, 2));
        continue;
      }
      const remoteStreamData = this.streamsCache.get(videoPresence.sessionId);
      // TODO: this code really assumes that each user connects with 2 tracks at beginning and then
      // only mutes/unmutes them. if we want to support adding/removing tracks we need to change this part
      if (remoteStreamData) {
        remoteStreamData.audioMuted = videoPresence.audioMuted;
        remoteStreamData.videoMuted = videoPresence.videoMuted;
      } else {
        newTracksToPull.push(item);
      }
    }

    // 3. find stale tracks of participants who left
    const personsSessionsIds = Object.values(others).map(({ videoPresence }) => videoPresence.sessionId);
    for (const [sessionId, remoteStreamData] of this.streamsCache.entries()) {
      if (!personsSessionsIds.includes(sessionId)) {
        const activeTracks = remoteStreamData.stream.getTracks();
        for (const activeTrack of activeTracks) {
          activeTrack.stop();
          remoteStreamData.stream.removeTrack(activeTrack);
        }
        if (remoteStreamData.stream.getTracks().length == 0) this.streamsCache.delete(sessionId);
      }
    }
    // I don't close the tracks the inactive tracks; cloudflare says it's not a must

    // let the outside world know about any changes at this point (mute status, and closed tracks)
    setRemoteStreams([...this.streamsCache.values()]);

    // queue up requests for new tracks
    if (newTracksToPull.length > 0) {
      log("will fetch tracks for", allUserNamesToString(newTracksToPull));
      for (const item of newTracksToPull) {
        if (!item.videoPresence.sessionId) {
          log("item without sessionId: should have been filtered before this point", JSON.stringify(item, null, 2));
          continue;
        }
        const remoteStreamData = VideoRoomHandler.buildCacheEntryForRemote(item);
        this.streamsCache.set(item.videoPresence.sessionId, remoteStreamData);
        this.jobQueue.push(remoteStreamData);
      }
      this.fetchNewTracksThread(setRemoteStreams);
    }
  }

  private static buildCacheEntryForRemote(data: RemoteUserVideoData) {
    const { clientId, videoPresence, userInfo } = data;
    const { sessionId, audioMuted, audioTrackName, videoMuted, videoTrackName } = videoPresence;
    const location = TrackObject.LocationEnum.Remote;
    const tracksInfo: TrackObject[] = [
      {
        location,
        sessionId,
        trackName: videoTrackName,
      },
      {
        location,
        sessionId,
        trackName: audioTrackName,
      },
    ];
    return {
      clientId,
      tracksInfo,
      userInfo,
      sessionId,
      stream: new MediaStream(),
      ready: false,
      videoMuted,
      audioMuted,
      videoPresence,
      retries: 0,
    } as RemoteStreamData;
  }

  private async fetchNewTracksThread(setRemoteStreams: (streams: RemoteStreamData[]) => void) {
    if (this.workerRunning) return;
    this.workerRunning = true;

    while (this.jobQueue.length > 0) {
      const tracks = [];
      let job;
      while ((job = this.jobQueue.shift())) {
        // cloudflare can only handle 64 tracks in a request
        if (tracks.length + job.tracksInfo.length >= 64) {
          this.jobQueue.unshift(job);
          break;
        }
        tracks.push(...job.tracksInfo);
      }
      // TODO: limit to 50 calls per second (do it in the Calls object)
      try {
        const addResponse = await this.call.addTracksToSession(tracks);
        // TODO: handle errors

        // Checking the response here.
        // sometimes we have stale tracks in reflect and when we try to
        // connect we get 'not_found_track_error'
        const [bad, good] = partition(propExists("errorCode"))(addResponse.tracks as TrackObject[]);

        // handle errors: empty_track_error, not_found_track_error
        // we can get errors here if remote side disconnected between the time we queued/sent the request and
        // the time cloudflare got it. there's nothing to do but ignore and continue
        if (bad.length > 0) {
          bad.forEach((item) => {
            if (item.sessionId && this.streamsCache.has(item.sessionId)) {
              const data = this.streamsCache.get(item.sessionId)!;
              if (data.retries < 5) {
                //TODO: enqueue request only for the failed tracks
                //TODO: enqueue retry only if the user is still in the room
                //TODO: enqueue retry after some time
                data.retries++;
                this.jobQueue.push(data);
              } else {
                // TODO: signal error to the user
                log("failed 5 retries for", data.userInfo.name, JSON.stringify(data, null, 2));
                this.streamsCache.delete(item.sessionId);
              }
            } else log("got item without sessionId in the bad list", JSON.stringify(item, null, 2));
          });
          // TODO: perhaps we should retry the failed tracks after 0.5 second
          setRemoteStreams([...this.streamsCache.values()]);
        }

        if (addResponse.requiresImmediateRenegotiation) {
          if (addResponse.sessionDescription!.type != "offer") {
            throw new Error("expected offer to come with renegotiation");
          }

          // prepare the event handler for tracks before asking for them
          let received = 0;
          const { promise, resolve } = resolver();
          const handleTrack = (event: RTCTrackEvent) => {
            received++;
            log(`got new track. has ${received} out of ${good.length}`);
            if (!event.transceiver.mid) {
              log("got track without mid from cloudflare", event.transceiver);
              return;
            }

            const trackDesc = find((item) => item.mid == event.transceiver.mid, good);
            const cacheEntry = mapGet(this.streamsCache, trackDesc?.sessionId);
            // cacheEntry can be null in a valid flow where we asked for a remote track, and the other
            // user disconnected before we got the track, and its entry was removed from the cache.
            const trackInfo = find((track) => track.trackName == trackDesc!.trackName, cacheEntry?.tracksInfo);
            if (trackInfo) {
              trackInfo.mid = event.transceiver.mid;
              if (DEBUG_VIDEO_ROOM) {
                event.track.addEventListener("mute", () => log("Track muted ", trackDesc?.trackName));
                event.track.addEventListener("unmute", () => log("Track resumed ", trackDesc?.trackName));
              }
              cacheEntry!.stream.addTrack(event.track);
              cacheEntry!.ready = cacheEntry!.stream.getTracks().length == cacheEntry!.tracksInfo.length;
              // TODO: I would love to notify the outside world of each ready stream at once,
              // and instead I notify only when I have responses for all.
              if (cacheEntry!.ready) log("received all tracks for user ", cacheEntry?.userInfo.name);
            }
            if (received >= good.length) {
              resolve();
            }
          };

          this.pc.addEventListener("track", handleTrack);

          await this.pc.setRemoteDescription(addResponse.sessionDescription! as any);

          await this.pc.setLocalDescription(await this.pc.createAnswer());

          await this.call.sendRenegotiateAnswer(this.pc.localDescription!.sdp);

          await Promise.race([promise, this.abortPromise.promise]); // wait until all tracks received, or cancel

          this.pc.removeEventListener("track", handleTrack);

          if (this.abort.signal.aborted) {
            log("aborting renegotiation");
            this.workerRunning = false;
            return;
          }

          log("received all tracks for all participants, calling setState", [...this.streamsCache.values()]);
          setRemoteStreams([...this.streamsCache.values()]);
        }
      } catch (error: any) {
        if (this.isCanceled(error)) break;
        log("error in fetchNewTracksThread", error.name, error.message, error.stack);
        console.trace(error);
      }
    }
    this.workerRunning = false;
  }
}
